import type { Family, FamilyMember, Permission, RoleWithPermissions } from "@mc/types"
import { apiRequest } from "./httpClient.js"

/** Shape returned by GET /api/v1/families/:familyId (services/gateway/src/routes/families.ts), mirrors @mc/db's FamilyMemberWithUser without importing a backend-only package from apps/web. */
export interface FamilyMemberEntry extends FamilyMember {
  username: string
  displayName: string | null
  avatarUrl: string | null
}

/** FR section 8: `/families`, `/families/new`, `/families/[id]`. */
export function listFamilies(): Promise<{ families: Family[] }> {
  return apiRequest("/api/v1/families")
}

export function createFamily(input: { name: string; description?: string | null; iconUrl?: string | null }): Promise<{ family: Family }> {
  return apiRequest("/api/v1/families", { method: "POST", body: input })
}

export function getFamily(familyId: string): Promise<{ family: Family; members: FamilyMemberEntry[] }> {
  return apiRequest(`/api/v1/families/${familyId}`)
}

export function updateFamily(
  familyId: string,
  input: { name?: string; description?: string | null; iconUrl?: string | null },
): Promise<{ family: Family }> {
  return apiRequest(`/api/v1/families/${familyId}`, { method: "PATCH", body: input })
}

export function deleteFamily(familyId: string): Promise<void> {
  return apiRequest(`/api/v1/families/${familyId}`, { method: "DELETE" })
}

export function setFamilyMemberRole(familyId: string, userId: string, roleId: string): Promise<void> {
  return apiRequest(`/api/v1/families/${familyId}/members`, { method: "PUT", body: { userId, roleId } })
}

export function removeFamilyMember(familyId: string, userId: string): Promise<void> {
  return apiRequest(`/api/v1/families/${familyId}/members/${userId}`, { method: "DELETE" })
}

/**
 * Family-scoped roles (FR section 4, same free-form role builder as
 * api/access.ts's server-scoped roles). Needed before any member can be
 * added: `setFamilyMemberRole`/`upsertFamilyMember` requires a roleId,
 * and no role exists for a new family until one is created here.
 */
export function listFamilyRoles(familyId: string): Promise<{ roles: RoleWithPermissions[] }> {
  return apiRequest(`/api/v1/families/${familyId}/roles`)
}

export function createFamilyRole(
  familyId: string,
  input: { name: string; color: string; permissions: Permission[] },
): Promise<{ role: RoleWithPermissions }> {
  return apiRequest(`/api/v1/families/${familyId}/roles`, { method: "POST", body: input })
}

export function updateFamilyRole(
  familyId: string,
  roleId: string,
  input: { name?: string; color?: string; permissions?: Permission[] },
): Promise<{ role: RoleWithPermissions }> {
  return apiRequest(`/api/v1/families/${familyId}/roles/${roleId}`, { method: "PATCH", body: input })
}

export function deleteFamilyRole(familyId: string, roleId: string): Promise<void> {
  return apiRequest(`/api/v1/families/${familyId}/roles/${roleId}`, { method: "DELETE" })
}
